"use client";

import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import Loader from "@/components/ui/loader";
import { clientEnv } from "@/config";
import { useVehicleTransferTaxPayMutation } from "@/hooks/useInquiry";
import { IBaseLabelValue } from "@/interfaces";
import {
  IRawPurchasedVehicleTransferTaxInquiryResData,
  IVehicleTransferTaxItem,
} from "@/interfaces/inquiries/vehicle";
import { formatNumberWithDots } from "@/lib";
import { routes } from "@/routes";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";

const taxTypeLabels: Record<number, string> = {
  1: "مالیات نقل و انتقال",
  2: "مالیات وسیله نقلیه نوشماره",
  3: "مالیات وسیله نقلیه لوکس",
  4: "مالیات بر مشاغل خودرویی",
  5: "مالیات بر ارث",
};

const getTaxTypeLabel = (taxType?: number) => {
  if (!taxType) return "-";

  return taxTypeLabels[taxType] ?? String(taxType);
};

const VehicleTransferTaxInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedVehicleTransferTaxInquiryResData;
}) => {
  const router = useRouter();
  const { data: session } = useSession();
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { isPending: isPaymentSubmitting, mutateAsync: payVehicleTax } =
    useVehicleTransferTaxPayMutation(session?.user.token);

  const inquiryData = data.data.data;
  const inquiryInput = data.data.input ?? (data as any).user_input ?? {};
  const taxItems = useMemo<IVehicleTransferTaxItem[]>(() => {
    if (!inquiryData?.taxes) return [];

    return Array.isArray(inquiryData.taxes)
      ? inquiryData.taxes
      : [inquiryData.taxes];
  }, [inquiryData?.taxes]);
  const payableTax = taxItems[0];
  const plateParts = [
    inquiryInput.plk_serial,
    inquiryInput.plk2,
    inquiryInput.plk3,
    inquiryInput.plk1,
  ].filter(Boolean);
  const plateText = plateParts.length ? plateParts.join(" ") : "-";

  const inputContent: IBaseLabelValue[] = [
    {
      label: "کد ملی مالک",
      value: inquiryInput.national_code || "-",
    },
    {
      label: "تاریخ تولد",
      value: inquiryInput.birth_date || "-",
    },
    {
      label: "شماره پلاک",
      value: plateText,
    },
  ];

  const taxDetails: IBaseLabelValue[] = [
    {
      label: "عنوان مالیات",
      value: payableTax?.name || "-",
    },
    {
      label: "وضعیت قبض",
      value: payableTax?.suspend ? "معلق" : "فعال",
    },
    {
      label: "نوع مالیات",
      value: getTaxTypeLabel(payableTax?.taxType),
    },
  ];

  const handleTaxPayment = async () => {
    if (!payableTax?.taxId) {
      setPaymentError("اطلاعات پرداخت مالیات کامل نیست.");
      return;
    }

    setPaymentError(null);
    const callbackUrl = new URL(
      `${clientEnv.NEXT_PUBLIC_BASE_URL}${routes.main.PAYMENT_CALLBACK.primaryPath}`,
    );
    callbackUrl.searchParams.set("service", "tax_vehicle");
    callbackUrl.searchParams.set("purchase_id", String(data.id));

    try {
      const response = await payVehicleTax({
        payload: {
          path: callbackUrl.toString(),
          national_code: inquiryInput.national_code,
          birth_date: inquiryInput.birth_date,
          citizen_type: inquiryInput.citizen_type || "2",
          plk1: inquiryInput.plk1,
          plk2: inquiryInput.plk2,
          plk3: inquiryInput.plk3,
          plk_serial: inquiryInput.plk_serial,
          campaign: inquiryInput.campaign,
        },
      });
      const paymentUrl = response.data?.url;

      if (!paymentUrl) {
        throw new Error("لینک پرداخت مالیات دریافت نشد.");
      }

      window.location.href = paymentUrl;
    } catch (submissionError) {
      setPaymentError(
        submissionError instanceof Error
          ? submissionError.message
          : "خطا در شروع پرداخت مالیات رخ داد.",
      );
    }
  };

  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          نتیجه استعلام مالیات نقل و انتقال خودرو
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />

        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">نتیجه استعلام</h2>
        </FlexBetween>

        {payableTax ? (
          <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-slate-500">مبلغ قابل پرداخت</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">
                  {formatNumberWithDots(payableTax.debit)} ریال
                </p>
              </div>
              <div className="w-fit rounded-full bg-emerald-100 px-4 py-2 text-sm font-medium text-emerald-700">
                {payableTax.name}
              </div>
            </div>

            <InputDataContent
              content={taxDetails}
              className="mb-5 p-5"
              itemClassName="flex-col md:flex-row leading-9 md:leading-none"
            />

            {!!paymentError && (
              <p className="mb-4 text-sm text-rose-600">{paymentError}</p>
            )}

            <CustomButton
              id="details-vehicle-transfer-tax-pay"
              variant="primary"
              className="w-full"
              disabled={isPaymentSubmitting}
              onClick={handleTaxPayment}
            >
              {isPaymentSubmitting
                ? "در حال انتقال به پرداخت..."
                : "پرداخت مالیات"}
            </CustomButton>
          </div>
        ) : (
          <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center text-sm text-slate-600">
            مالیاتی برای پرداخت یافت نشد.
          </div>
        )}

        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <CustomButton
            id="details-vehicle-transfer-tax-back"
            className="w-fit bg-gray-500"
            onClick={() => router.push(routes.main.INQUIRY_HISTORY.primaryPath)}
          >
            بازگشت
          </CustomButton>
          <CustomButton
            id="details-vehicle-transfer-tax-new-request"
            onClick={() =>
              router.push(routes.services.Vehicle_Transfer_Tax.rewritePath)
            }
            className="bg-primary-main mx-auto w-fit px-8! text-white"
          >
            استعلام جدید
          </CustomButton>
        </FlexCenter>
      </FlexCol>

      {isPaymentSubmitting && (
        <div className="flex-center fixed inset-0 z-[1000] flex bg-gray-900/60">
          <div className="flex-column flex-center flex w-60 rounded-xl bg-white p-3 sm:w-80 sm:p-4">
            <h2 className="text-center text-sm font-semibold sm:text-base">
              در حال انتقال به پرداخت
            </h2>
            <div className="mt-4 size-4 sm:size-5">
              <Loader color="bg-primary-main" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleTransferTaxInquiryDetails;
